import { motion } from '@/lib/motion';
import { Brain, FileText, MessageSquare, PhoneCall, Upload } from 'lucide-react';

/**
 * "One brain, two channels" — the core pitch as a diagram: train once (upload
 * docs/URLs) → one knowledge base → answers on voice and chat. The shared
 * knowledge core in the middle is the product's defensible differentiator.
 * Pure layout + entrance animation; honors reduced-motion via MotionConfig.
 */
export function KnowledgeCoreDiagram(): JSX.Element {
  return (
    <section className="container py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
          One brain, two channels
        </span>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          Train once. Answer everywhere.
        </h2>
        <p className="mt-4 text-muted-foreground">
          Upload your docs, URLs, and FAQs a single time. The same knowledge base
          powers your phone agent and your website chat — identical answers, identical brand.
        </p>
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
        className="mt-16 grid grid-cols-1 items-center gap-8 md:grid-cols-3"
      >
        {/* Train once */}
        <Node
          tone="neutral"
          icon={<Upload className="h-6 w-6" />}
          title="Train once"
          desc="Upload PDFs, paste URLs, add FAQs. No code."
          chips={['Docs', 'URLs', 'FAQs']}
        />

        {/* Shared knowledge core */}
        <motion.div
          variants={fade}
          className="relative flex flex-col items-center"
        >
          {/* Connector lines (desktop) */}
          <span className="pointer-events-none absolute right-full top-1/2 hidden h-px w-8 -translate-y-1/2 bg-gradient-to-r from-transparent to-[var(--brand-accent)]/50 md:block" />
          <span className="pointer-events-none absolute left-full top-1/2 hidden h-px w-8 -translate-y-1/2 bg-gradient-to-l from-transparent to-[var(--brand-accent)]/50 md:block" />
          <div className="relative flex h-28 w-28 items-center justify-center rounded-3xl bg-gradient-to-br from-[var(--brand-accent)] to-fuchsia-500 text-white shadow-xl shadow-[var(--brand-accent)]/30">
            <span className="absolute inset-0 rounded-3xl bg-[var(--brand-accent)]/40 blur-xl" />
            <Brain className="relative h-12 w-12" />
          </div>
          <p className="mt-4 text-center text-sm font-semibold">
            One knowledge base
          </p>
          <p className="text-center text-xs text-muted-foreground">RAG core</p>
        </motion.div>

        {/* Answer on both channels */}
        <motion.div variants={fade} className="flex flex-col gap-4">
          <ChannelPill
            icon={<PhoneCall className="h-5 w-5" />}
            tone="accent"
            title="Answer on voice"
            desc="Every inbound phone call."
          />
          <ChannelPill
            icon={<MessageSquare className="h-5 w-5" />}
            tone="emerald"
            title="Answer on chat"
            desc="Every website visitor."
          />
        </motion.div>
      </motion.div>
    </section>
  );
}

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

function Node({
  icon,
  title,
  desc,
  chips,
}: {
  tone: 'neutral';
  icon: React.ReactNode;
  title: string;
  desc: string;
  chips: string[];
}): JSX.Element {
  return (
    <motion.div
      variants={fade}
      className="rounded-2xl border border-border bg-card p-6 text-center"
    >
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-foreground">
        {icon}
      </div>
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {chips.map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
          >
            <FileText className="h-3 w-3" />
            {c}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

function ChannelPill({
  icon,
  title,
  desc,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  tone: 'accent' | 'emerald';
}): JSX.Element {
  const toneCls =
    tone === 'accent'
      ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]'
      : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
      <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${toneCls}`}>
        {icon}
      </span>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}
